import { useEffect, useState, useCallback } from "react";
import { format } from "date-fns";
import Layout from "../layouts/layout";
import Loader from "../components/loader";
import MESSAGE_LOG from "../api/services/MESSAGE_LOG";
import styles from "../styles/_screens/messageHistory.module.scss";

const CHANNEL_ICON = { whatsapp: "💬", sms: "📱" };
const CHANNEL_LABEL = { whatsapp: "WhatsApp", sms: "SMS" };

const TYPE_LABEL = {
  reminder_24h: "תזכורת 24 שעות",
  reminder_3h: "תזכורת 3 שעות",
  booking_confirmation: "אישור הזמנה",
  cancellation: "ביטול תור",
  automation: "אוטומציה",
  bulk: "שיווקית",
};

const FILTERS = [
  { key: "all", label: "הכל" },
  { key: "reminder_24h", label: "תזכורות 24ש'" },
  { key: "reminder_3h", label: "תזכורות 3ש'" },
  { key: "booking_confirmation", label: "אישורי הזמנה" },
  { key: "cancellation", label: "ביטולים" },
  { key: "automation", label: "אוטומציות" },
  { key: "bulk", label: "שיווקיות" },
];

const LIMIT = 30;

const MessageHistory = () => {
  const [allLogs, setAllLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [activeFilter, setActiveFilter] = useState("all");

  const fetchLogs = useCallback(async (pageNum = 1, append = false) => {
    if (pageNum === 1) setLoading(true);
    else setLoadingMore(true);

    try {
      const res = await MESSAGE_LOG.get_message_log(pageNum, LIMIT);
      const data = res?.data || [];
      const totalCount = res?.total || 0;
      setAllLogs((prev) => (append ? [...prev, ...data] : data));
      setTotal(totalCount);
      setPage(pageNum);
    } catch (e) {
      console.log("messageHistory fetch error:", e);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs(1);
  }, [fetchLogs]);

  // Client-side filter (filter from already-fetched logs)
  const filtered =
    activeFilter === "all"
      ? allLogs
      : allLogs.filter((l) => l.type === activeFilter);

  const hasMore = allLogs.length < total;

  if (loading) {
    return (
      <Layout>
        <div className={styles.container}>
          <div className={styles.wrapper}>
            <div className={styles.main}>
              <Loader />
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={styles.container}>
        <div className={styles.wrapper}>
          <div className={styles.main}>

            {/* Header */}
            <div className={styles.header}>
              <h1>היסטוריית הודעות יוצאות</h1>
              <div className={styles.stats}>
                <div className={styles.statBadge}>
                  סה"כ: <span>{total}</span>
                </div>
                <div className={styles.statBadge}>
                  נכשלו:{" "}
                  <span style={{ color: "#dc2626" }}>
                    {allLogs.filter((l) => l.status === "failed").length}
                  </span>
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className={styles.filters}>
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  className={`${styles.filterBtn} ${activeFilter === f.key ? styles.active : ""}`}
                  onClick={() => setActiveFilter(f.key)}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {/* List */}
            <div className={styles.items}>
              {filtered.length === 0 ? (
                <div className={styles.empty}>אין הודעות להצגה</div>
              ) : (
                filtered.map((item) => (
                  <div className={styles.item} key={item._id}>
                    <div className={styles.channelIcon}>
                      {CHANNEL_ICON[item.channel] || "📨"}
                    </div>

                    <div className={styles.info}>
                      <div className={styles.topRow}>
                        <span className={styles.name}>
                          {item.recipientName || "—"}
                        </span>
                        {item.recipientPhone && (
                          <span className={styles.phone}>
                            {item.recipientPhone}
                          </span>
                        )}
                      </div>
                      <div className={styles.typeRow}>
                        <span className={styles.typeBadge}>
                          {item.typeLabel || TYPE_LABEL[item.type] || item.type}
                        </span>
                        <span className={styles.channelBadge}>
                          {CHANNEL_LABEL[item.channel] || item.channel}
                        </span>
                      </div>
                      {item.preview && (
                        <p className={styles.preview}>{item.preview}</p>
                      )}
                    </div>

                    <div className={styles.right}>
                      <span
                        className={`${styles.statusBadge} ${
                          item.status === "sent" ? styles.sent : styles.failed
                        }`}
                      >
                        {item.status === "sent" ? "נשלח ✓" : "נכשל ✗"}
                      </span>
                      <span className={styles.date}>
                        {format(new Date(item.createdAt), "dd/MM/yy HH:mm")}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Load more */}
            {hasMore && (
              <div className={styles.loadMore}>
                <button
                  onClick={() => fetchLogs(page + 1, true)}
                  disabled={loadingMore}
                >
                  {loadingMore ? "טוען..." : "טען עוד"}
                </button>
              </div>
            )}

          </div>
        </div>
      </div>
    </Layout>
  );
};

export default MessageHistory;
